import type { GeoJSONSource } from 'maplibre-gl';
import { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@/providers/theme-provider';
import { useMap } from './map-context';
import { mapPalette } from './map-palette';

/**
 * A polyline plus optional endpoint dots. Two instances cooperate on the trip
 * replay screen: one drawing the whole route faintly, one drawing the travelled
 * portion in full colour.
 *
 * Adding the layers and updating their data are deliberately separate effects.
 * Keeping `data` in the add-effect's deps would remove and re-add the source on
 * every replay frame, and the layers would never live long enough to draw.
 */
export function RouteLayer({
  id,
  coordinates,
  tone = 'primary',
  width = 3,
  opacity = 0.9,
  showEndpoints = false,
}: {
  id: string;
  coordinates: [number, number][];
  tone?: 'primary' | 'replay' | 'muted';
  width?: number;
  opacity?: number;
  showEndpoints?: boolean;
}) {
  const { map, ready, styleEpoch } = useMap();
  const { resolved } = useTheme();

  const sourceId = `vt-route-${id}`;
  const casingId = `${sourceId}-casing`;
  const lineId = `${sourceId}-line`;
  const pointsId = `${sourceId}-points`;

  const data = useMemo<GeoJSON.FeatureCollection>(() => {
    const features: GeoJSON.Feature[] = [];
    if (coordinates.length > 1) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates },
        properties: {},
      });
    }
    if (showEndpoints && coordinates.length > 0) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: coordinates[0] },
        properties: { kind: 'start' },
      });
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: coordinates[coordinates.length - 1] },
        properties: { kind: 'end' },
      });
    }
    return { type: 'FeatureCollection', features };
  }, [coordinates, showEndpoints]);

  // Seeds the source on creation without making the add-effect depend on data.
  const initialData = useRef(data);

  useEffect(() => {
    if (!map || !ready) return;
    const palette = mapPalette(resolved);
    const color =
      tone === 'replay' ? palette.replay : tone === 'muted' ? palette.trail : palette.trail;

    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'geojson', data: initialData.current });
    }

    if (!map.getLayer(casingId)) {
      map.addLayer({
        id: casingId,
        type: 'line',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': palette.trailCasing,
          'line-width': width + 3,
          'line-opacity': opacity * 0.7,
        },
      });
    }

    if (!map.getLayer(lineId)) {
      map.addLayer({
        id: lineId,
        type: 'line',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': color, 'line-width': width, 'line-opacity': opacity },
      });
    }

    if (showEndpoints && !map.getLayer(pointsId)) {
      map.addLayer({
        id: pointsId,
        type: 'circle',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 5,
          'circle-color': ['case', ['==', ['get', 'kind'], 'start'], palette.replay, color],
          'circle-stroke-width': 2,
          'circle-stroke-color': palette.trailCasing,
        },
      });
    }

    return () => {
      for (const layer of [pointsId, lineId, casingId]) {
        if (map.getLayer(layer)) map.removeLayer(layer);
      }
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [
    map,
    ready,
    styleEpoch,
    resolved,
    tone,
    width,
    opacity,
    showEndpoints,
    sourceId,
    casingId,
    lineId,
    pointsId,
  ]);

  useEffect(() => {
    if (!map || !ready) return;
    (map.getSource(sourceId) as GeoJSONSource | undefined)?.setData(data);
  }, [map, ready, styleEpoch, sourceId, data]);

  return null;
}
